// "Weekly reports" — Practice Details card, admin only (the backend refuses
// everyone else). The weekly client email (the "Weekly report, client" section
// under the Waterfall, mailed to the practice's own recipients on the chosen
// weekday and hour, practice time) and the weekly team Slack post (the team
// section's figures, counts and dollars only, to the Atlas ledger channel). Both
// off for every practice until someone turns them on here; the jobs themselves
// also wait on their own Terraform switch (docs/weekly-reports-delivery.md).
// "Send test now" sends the client email to the saved recipients, or posts this
// practice's team report to Slack, whether or not the switch is on.
//
// Nothing here names a patient: the card reads switches, addresses, a weekday,
// an hour and a channel label.

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./WeeklyReports.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const DAYS = [
  [1, "Monday"],
  [2, "Tuesday"],
  [3, "Wednesday"],
  [4, "Thursday"],
  [5, "Friday"],
  [6, "Saturday"],
  [7, "Sunday"],
];

const url = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/digest/delivery`;

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

export function fetchWeeklyReports(practiceId) {
  return request(url(practiceId), {}, "Could not load the weekly report settings");
}

export function saveWeeklyReports(practiceId, body) {
  return request(
    url(practiceId),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Could not save the weekly report settings",
  );
}

export function sendTest(practiceId, which) {
  return request(
    `${url(practiceId)}/test`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ which }),
    },
    "The test could not be sent",
  );
}

export function parseRecipients(text) {
  return String(text || "")
    .split(/[,\n;]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export function hourLabel(h) {
  const n = Number(h);
  const suffix = n < 12 ? "am" : "pm";
  const twelve = n % 12 === 0 ? 12 : n % 12;
  return `${String(n).padStart(2, "0")}:00 (${twelve} ${suffix})`;
}

// `children` render under the two sends (Practice Details puts the client feedback
// there: the answers come back from the weekly client email). `showSave={false}`
// drops the card's own Save: the page saves it through the ref (`ref.current.save()`,
// resolves when saved, throws with the server's message), with one Save for the tab.
const WeeklyReportsCard = forwardRef(function WeeklyReportsCard(
  { practiceId, readOnly = false, children = null, showSave = true },
  ref,
) {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setSettings(null);
    setForm(null);
    setError(null);
    setMessage(null);
    fetchWeeklyReports(practiceId)
      .then((body) => {
        if (cancelled) return;
        setSettings(body);
        setForm({
          client_report_email_enabled: !!body.client_report_email_enabled,
          client_report_email_recipients: (body.client_report_email_recipients || []).join(", "),
          client_report_email_day: Number(body.client_report_email_day) || 1,
          client_report_email_hour: Number(body.client_report_email_hour) || 0,
          team_report_slack_enabled: !!body.team_report_slack_enabled,
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  // Nothing to save until the settings have loaded and their migration has run.
  const persist = async () => {
    if (!form || (settings && settings.migration_pending)) return;
    const body = await saveWeeklyReports(practiceId, {
      client_report_email_enabled: form.client_report_email_enabled,
      client_report_email_recipients: parseRecipients(form.client_report_email_recipients),
      client_report_email_day: Number(form.client_report_email_day),
      client_report_email_hour: Number(form.client_report_email_hour),
      team_report_slack_enabled: form.team_report_slack_enabled,
    });
    if (currentPractice.current === practiceId) setSettings(body);
  };

  useImperativeHandle(ref, () => ({ save: persist }));

  const save = async () => {
    if (busy || !form) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await persist();
      if (currentPractice.current === practiceId) setMessage("Saved.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // What the Slack test did: the table block, the text fallback Slack took instead,
  // or only the "no feed" line when this practice has no payments feed.
  const slackTestMessage = (out, id) => {
    if ((out.skipped || []).includes(id)) {
      return `Nothing to post for this practice: it has no payments feed, so Slack got the "no feed" line only (${out.label}).`;
    }
    const rendering = (out.renderings || {})[String(id)];
    const how = rendering === "text" ? " as text (Slack refused the table block)" : "";
    return `Test posted to Slack${how} (${out.label}).`;
  };

  const test = async (which) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const out = await sendTest(practiceId, which);
      if (currentPractice.current !== practiceId) return;
      setMessage(which === "client_email" ? `Test email sent to ${(out.recipients || []).join(", ")} (${out.label}).` : slackTestMessage(out, practiceId));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pending = settings && settings.migration_pending;
  const disabled = busy || readOnly || !form || pending;

  // Laid out like the Daily email section above it (2026-10-25): a label column, then
  // Enabled, Recipients, when it goes, and the Slack post.
  return (
    <div className="integrations-section weekly-reports" data-testid="weekly-reports-card">
      <h3 className="practice-rules-section-title">Weekly report</h3>
      {pending ? (
        <p className="weekly-reports-pending" data-testid="weekly-reports-pending">
          Unavailable until migration {settings.migration} has run.
        </p>
      ) : null}
      {error && (
        <div className="weekly-reports-error" role="alert">
          {error}
          <button type="button" className="weekly-reports-dismiss" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
      {message && (
        <div className="weekly-reports-ok" data-testid="weekly-reports-message">
          {message}
          <button type="button" className="weekly-reports-dismiss" onClick={() => setMessage(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
      {form ? (
        <div className="weekly-reports-form">
          <div className="wr-row">
            <label className="wr-label" htmlFor="weekly-client-enabled">Enabled?</label>
            <input
              id="weekly-client-enabled"
              type="checkbox"
              checked={form.client_report_email_enabled}
              onChange={(e) => setForm({ ...form, client_report_email_enabled: e.target.checked })}
              disabled={disabled}
              data-testid="weekly-client-enabled"
            />
          </div>
          <div className="wr-row">
            <label className="wr-label" htmlFor="weekly-client-recipients">Recipients</label>
            <input
              id="weekly-client-recipients"
              type="text"
              className="wr-input"
              value={form.client_report_email_recipients}
              onChange={(e) => setForm({ ...form, client_report_email_recipients: e.target.value })}
              placeholder="owner@practice…, office@practice…"
              disabled={disabled}
              data-testid="weekly-client-recipients"
            />
          </div>
          <div className="wr-row">
            <span className="wr-label">Send On</span>
            <select
              aria-label="Day"
              value={form.client_report_email_day}
              onChange={(e) => setForm({ ...form, client_report_email_day: Number(e.target.value) })}
              disabled={disabled}
              data-testid="weekly-client-day"
            >
              {DAYS.map(([n, name]) => (
                <option key={n} value={n}>
                  {name}
                </option>
              ))}
            </select>
            <span className="wr-muted">at</span>
            <select
              aria-label="Hour"
              value={form.client_report_email_hour}
              onChange={(e) => setForm({ ...form, client_report_email_hour: Number(e.target.value) })}
              disabled={disabled}
              data-testid="weekly-client-hour"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
            <span className="wr-muted">{settings ? settings.timezone : ""}</span>
          </div>
          {!readOnly && (
            <div className="wr-row">
              <span className="wr-label" />
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => test("client_email")}
                disabled={busy || pending}
                data-testid="weekly-client-test"
                title="Sends the client report for the last complete week to the SAVED recipients now"
              >
                Send Sample E-mail
              </button>
            </div>
          )}
          <div className="wr-row">
            <label className="wr-label" htmlFor="weekly-slack-enabled">Team Slack post</label>
            <input
              id="weekly-slack-enabled"
              type="checkbox"
              checked={form.team_report_slack_enabled}
              onChange={(e) => setForm({ ...form, team_report_slack_enabled: e.target.checked })}
              disabled={disabled}
              data-testid="weekly-slack-enabled"
            />
            <span className="wr-muted">
              Mondays, to the Atlas ledger channel (counts and dollars, no patients)
            </span>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => test("team_slack")}
                disabled={busy || pending}
                data-testid="weekly-slack-test"
              >
                Send test post
              </button>
            )}
          </div>
        </div>
      ) : !error ? (
        <p className="weekly-reports-muted">Loading…</p>
      ) : null}
      {children}
      {form && !readOnly && showSave && (
        <div className="weekly-reports-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={disabled}
            data-testid="weekly-reports-save"
          >
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </div>
  );
});

export default WeeklyReportsCard;
